import React from "react";

/**
 * Contentful Markdown "Insert link":
 * [Link text](https://example.com "Link title")
 * [Link text](https://example.com)
 */
const MARKDOWN_LINK_PATTERN =
  /\[([^\]]+)\]\(\s*(https?:\/\/[^)\s]+|www\.[^)\s]+)\s*(?:"([^"]*)"|'([^']*)')?\s*\)/gi;

/** Accidental plain URLs: http(s)://… or www.… until whitespace or `)`. */
const PLAIN_TEXT_URL_PATTERN = /https?:\/\/[^\s)]+|www\.[^\s)]+/gi;

function toHref(rawUrl: string): string {
  if (/^https?:\/\//i.test(rawUrl)) return rawUrl;
  return `https://${rawUrl}`;
}

function toDisplayText(rawUrl: string): string {
  return rawUrl.replace(/^https?:\/\//i, "");
}

function renderAnchor(
  key: number,
  href: string,
  children: React.ReactNode,
  title?: string,
): React.ReactNode {
  return (
    <a
      key={key}
      href={href}
      title={title || undefined}
      target="_blank"
      rel="noopener noreferrer"
      className="event-description-link"
    >
      {children}
    </a>
  );
}

/** Linkify accidental plain URLs inside a text segment (no markdown links). */
function linkifyPlainUrls(text: string, keyOffset: { current: number }): React.ReactNode[] {
  const nodes: React.ReactNode[] = [];
  let lastIndex = 0;
  const pattern = new RegExp(
    PLAIN_TEXT_URL_PATTERN.source,
    PLAIN_TEXT_URL_PATTERN.flags,
  );

  let match: RegExpExecArray | null;
  while ((match = pattern.exec(text)) !== null) {
    const rawUrl = match[0];
    const urlStart = match.index;
    const urlEnd = urlStart + rawUrl.length;

    const hasOpenParen = urlStart > 0 && text[urlStart - 1] === "(";
    const hasCloseParen = urlEnd < text.length && text[urlEnd] === ")";
    const wrapped = hasOpenParen && hasCloseParen;

    const segmentStart = wrapped ? urlStart - 1 : urlStart;
    const segmentEnd = wrapped ? urlEnd + 1 : urlEnd;

    if (segmentStart > lastIndex) {
      nodes.push(
        <React.Fragment key={keyOffset.current++}>
          {text.slice(lastIndex, segmentStart)}
        </React.Fragment>,
      );
    }

    nodes.push(
      renderAnchor(
        keyOffset.current++,
        toHref(rawUrl),
        wrapped ? `(${toDisplayText(rawUrl)})` : toDisplayText(rawUrl),
      ),
    );

    lastIndex = segmentEnd;
    pattern.lastIndex = segmentEnd;
  }

  if (lastIndex < text.length) {
    nodes.push(
      <React.Fragment key={keyOffset.current++}>
        {text.slice(lastIndex)}
      </React.Fragment>,
    );
  }

  return nodes;
}

/**
 * Renders event description text with:
 * 1. Contentful Markdown links — `[text](url "title")` (link text + optional hover title)
 * 2. Accidental plain URLs — `http(s)://…` or `www.…`
 */
export function linkifyPlainText(text: string): React.ReactNode {
  if (!text) return null;

  const nodes: React.ReactNode[] = [];
  const keyOffset = { current: 0 };
  let lastIndex = 0;
  const mdPattern = new RegExp(
    MARKDOWN_LINK_PATTERN.source,
    MARKDOWN_LINK_PATTERN.flags,
  );

  let match: RegExpExecArray | null;
  while ((match = mdPattern.exec(text)) !== null) {
    const [fullMatch, linkText, rawUrl, titleDouble, titleSingle] = match;
    const title = titleDouble || titleSingle || undefined;
    const matchStart = match.index;
    const matchEnd = matchStart + fullMatch.length;

    if (matchStart > lastIndex) {
      nodes.push(
        ...linkifyPlainUrls(text.slice(lastIndex, matchStart), keyOffset),
      );
    }

    nodes.push(
      renderAnchor(
        keyOffset.current++,
        toHref(rawUrl),
        linkText,
        title,
      ),
    );

    lastIndex = matchEnd;
  }

  if (lastIndex < text.length) {
    nodes.push(...linkifyPlainUrls(text.slice(lastIndex), keyOffset));
  }

  return nodes.length > 0 ? nodes : null;
}
